/**
 * The Ways REST API (hs-ways-to-work, ARCHITECTURE.md §12) for the PMO Ways module.
 *
 * Every call carries the `wtw_` session token; writes that act in PMO (messages tied to a work
 * item, decisions) also carry the person's hs-auth token as `X-HS-Identity` (D-6). Every refusal
 * is JSON `{error, message}`. A 401 re-mints the session once, then asks for a sign-in.
 */
import { WAYS_API_URL, WaysSignInRequired, getIdentity, getSession } from './waysAuth';

export class WaysError extends Error {
  status: number;
  code: string;
  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'WaysError';
    this.status = status;
    this.code = code;
  }
}

export interface WaysChannel {
  id?: string;
  key: string;
  name: string;
  kind: string;
  projectId?: string | null;
  epicId?: string | null;
  readOnly?: boolean;
  unread?: number;
}

export interface WaysAuthor {
  text: string;
  initials?: string;
  sub?: string;
  actorClass?: string;
  session?: string;
  workspace?: string;
  repository?: string;
  host?: string;
  client?: string;
  declared?: string[];
}

export interface WaysChainLink {
  level: 'po' | 'epic' | 'feature' | 'story' | 'task' | string;
  id?: string;
  display?: string;
}

export interface WaysMessage {
  id: string;
  channel: string;
  seq: number;
  kind: string;
  ts: string;
  text: string;
  headline?: string;
  needsReply: boolean;
  replyTo?: string | null;
  to: string[];
  author: WaysAuthor;
  chain: WaysChainLink[];
  workResolved: boolean;
  links: Record<string, unknown>;
  hash?: string;
}

async function call<T>(method: string, path: string, body?: unknown, actsInPmo = false): Promise<T> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      Authorization: `Bearer ${await getSession(attempt > 0)}`,
    };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (actsInPmo) headers['X-HS-Identity'] = `Bearer ${await getIdentity()}`;

    let res: Response;
    try {
      res = await fetch(`${WAYS_API_URL}/api${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      throw new WaysError('Ways is unreachable; try again shortly', 0, 'unreachable');
    }
    const data = await res.json().catch(() => ({}));
    if (res.ok) return data as T;
    if (res.status === 401 && attempt === 0 && data.error !== 'identity_mismatch') continue;
    if (res.status === 401) throw new WaysSignInRequired(data.message);
    throw new WaysError(data.message || data.error || `Ways answered ${res.status}`, res.status, data.error || '');
  }
  throw new WaysSignInRequired();
}

const enc = encodeURIComponent;

export const waysApi = {
  channels: () => call<{ channels: WaysChannel[] }>('GET', '/channels'),

  /** What needs THIS person's response (hs-pmo/ep19#47): asks with their questions, holds. */
  // Ways 0.35 answers `responses` (asks and holds) and `approvals`; `items` is the older name.
  /** Mine and my projects' asks by default; scope 'all' adds others' for super admins (Ways 0.36.19). */
  inboxDecide: (scope: 'mine' | 'all' = 'mine') => call<{
    at: string; responses?: WaysDecideItem[]; items?: WaysDecideItem[];
    scope?: 'mine' | 'all'; canViewAll?: boolean; othersCount?: number;
  }>('GET', `/inbox/decide${scope === 'all' ? '?scope=all' : ''}`),
  /** Answer some or all of an ask's questions; recorded as one reply in its thread. */
  answerAsk: (id: string, answers: { id: string; value: string | boolean }[], note?: string) =>
    call<{ status: string; questions: WaysQuestion[] }>('POST', '/asks/answers', { id, answers, ...(note ? { note } : {}) }, true),

  /** `after` (Ways 0.36.15+): only messages newer than that seq; one cheap read when nothing is new,
   *  and it does not ack (hs-pmo/ep43#35 f). */
  messages: (channel: string, opts: { before?: number; after?: number; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (opts.before) q.set('before', String(opts.before));
    if (opts.after) q.set('after', String(opts.after));
    if (opts.limit) q.set('limit', String(opts.limit));
    const qs = q.toString();
    return call<{ channel: WaysChannel; messages: WaysMessage[] }>(
      'GET', `/channels/${enc(channel)}/messages${qs ? `?${qs}` : ''}`,
    );
  },

  inbox: () => call<Record<string, unknown>>('GET', '/inbox'),
  ack: (marks: Record<string, number>) => call<Record<string, unknown>>('POST', '/inbox/ack', { marks }),
  pending: () => call<Record<string, unknown>>('GET', '/pending'),
  awaiting: () => call<Record<string, unknown>>('GET', '/awaiting'),

  /** A note, ask, reply, handoff or item request. Carries the identity: it may act in PMO. */
  send: (msg: Record<string, unknown>) => call<Record<string, unknown>>('POST', '/messages', msg, true),
  reply: (id: string, status: 'done' | 'partial' | 'skip', note?: string) =>
    call<Record<string, unknown>>('POST', '/replies', { id, status, ...(note ? { note } : {}) }),

  requestDecision: (d: Record<string, unknown>) => call<Record<string, unknown>>('POST', '/decisions', d, true),
  decision: (item: string) => call<Record<string, unknown>>('GET', `/decisions/${enc(item)}`),

  conversation: (workItemId: string) =>
    call<{ item: string; messages: WaysMessage[] }>('GET', `/items/${enc(workItemId)}/conversation`),

  // ── The person's sidebar (Ways 0.8.0): sections in Ways' order, per person ──
  /** The projects view. Ways 0.36.15+ windows it to the last 24 h: Active pages 25 at a time
   *  (`activeOffset`), General lists active projects unless `all` (window=0) (hs-pmo/ep43#35 e). */
  sidebar: (opts: { activeOffset?: number; all?: boolean } = {}) => {
    const q = new URLSearchParams({ view: 'projects' });
    if (opts.activeOffset) q.set('activeOffset', String(opts.activeOffset));
    if (opts.all) q.set('window', '0');
    return call<{ sections: WaysSection[]; collapsed?: string[]; expanded?: string[] }>('GET', `/sidebar?${q}`);
  },
  /** One project's channels, read when its row is opened (Ways 0.9.0). 404: not readable. */
  projectChannels: (projectId: string) =>
    call<{ channels: WaysSidebarChannel[]; closedEpics?: number; showClosed?: boolean }>(
      'GET', `/sidebar/projects/${enc(projectId)}`),
  /** Show or hide a project's closed (read-only) epic channels; remembered per person per project. */
  setShowClosedEpics: (projectId: string, show: boolean) =>
    call<Record<string, unknown>>('PUT', '/sidebar/closed-epics', { project: projectId, show }),
  /** Fold or unfold one node of the person's sidebar; remembered by Ways across devices. */
  setCollapsed: (node: string, collapsed: boolean) =>
    call<Record<string, unknown>>('PUT', '/sidebar/collapsed', { node, collapsed }),
  createSection: (title: string) => call<{ section: WaysSection }>('POST', '/sidebar/sections', { title }),
  updateSection: (id: string, patch: { title?: string; position?: number }) =>
    call<{ section: WaysSection }>('PATCH', `/sidebar/sections/${enc(id)}`, patch),
  deleteSection: (id: string) => call<Record<string, unknown>>('DELETE', `/sidebar/sections/${enc(id)}`),
  addToSection: (section: string, type: 'channel' | 'conversation', id: string, position?: number) =>
    call<Record<string, unknown>>('POST', `/sidebar/sections/${enc(section)}/items`,
      { type, id, ...(position !== undefined ? { position } : {}) }),
  removeFromSection: (section: string, type: 'channel' | 'conversation', id: string) =>
    call<Record<string, unknown>>('DELETE', `/sidebar/sections/${enc(section)}/items?type=${enc(type)}&id=${enc(id)}`),

  // ── Governance (Ways 0.11): off switches, the governor's mode, its findings ──
  killswitches: () => call<{ switches: WaysSwitch[] }>('GET', '/killswitch'),
  /** Stop (on) or resume (off) agents -- or everyone -- globally, in a project or in one session. */
  setKillswitch: (b: { scope: 'global' | 'project' | 'session'; id?: string; on: boolean; actors?: 'agents' | 'all'; reason: string }) =>
    call<{ switch: WaysSwitch; switches: WaysSwitch[] }>('PUT', '/killswitch', b),
  governanceMode: (project: string) =>
    call<{ project: string; mode: 'shadow' | 'advisory' }>('GET', `/governance/mode?project=${enc(project)}`),
  setGovernanceMode: (project: string, mode: 'shadow' | 'advisory') =>
    call<{ project: string; mode: string }>('PUT', '/governance/mode', { project, mode }),
  findings: (project: string) =>
    call<{ mode: string; findings: WaysFinding[] }>('GET', `/governance/findings?project=${enc(project)}`),

  /** Decide a held agent action (D-7). It runs once, under the decider's authority. */
  releaseHold: (holdId: string, decision: 'allow' | 'refuse', reason?: string) =>
    call<Record<string, unknown>>('POST', `/holds/${enc(holdId)}/release`,
      { decision, ...(reason ? { reason } : {}) }, true),

  /** The person's Claude account for runner sessions (PO87-120). Only from this human browser session;
   *  the token is sent once and never comes back -- status shows a fingerprint only. */
  /** The agents' working-practice policy, effective for a project (its overrides over the default). */
  policy: (project: string) => call<WaysPolicy>('GET', `/policy?project=${enc(project)}`),
  /** Set policy keys for one project, or the default for all projects when `project` is omitted (Ways admin). */
  setPolicy: (b: { project?: string; rules?: Partial<Record<WaysPolicyKey, { text: string } | null>>; extra?: WaysExtraRule[] }
    & Partial<Record<WaysPolicyKey, string>>) =>
    call<WaysPolicy>('PUT', '/policy', b),
  /** Live runner sessions (PO92-225): Ways admins see all, a project's moderators theirs. */
  /** Live runners and sessions' own subagents; `ended` (Ways 0.36.12+) also returns runs that
   *  ended in the last day, `alive: false`, for a separate "Recently ended" list. */
  runnerSessions: (project?: string, ended = false) => {
    const q = [project && `project=${enc(project)}`, ended && 'include=ended'].filter(Boolean).join('&');
    return call<{ sessions: WaysRunnerSession[]; at: number | string }>('GET', `/runner/sessions${q ? `?${q}` : ''}`);
  },
  terminateRunner: (id: string, reason: string) =>
    call<Record<string, unknown>>('POST', `/runner/sessions/${enc(id)}/terminate`, { reason }),
  /** Tokens measured by claude-runner. */
  runnerUsage: (days: number, project?: string) =>
    call<WaysUsage<WaysRunnerUsageSession>>(
      'GET', `/runner/usage?days=${days}${project ? `&project=${enc(project)}` : ''}`),
  /** Tokens REPORTED by interactive sessions through the hsinc plugin: self-reported, not measured. */
  reportedUsage: (days: number, project?: string) =>
    call<WaysUsage<WaysReportedSession> & { reported: true }>(
      'GET', `/usage/sessions?days=${days}${project ? `&project=${enc(project)}` : ''}`),
  /** Presence (Ways 0.30+, PO-92 EP-5 FE-3): who holds each item and who is ACTIVELY working it,
   *  mirrored from PMO's checkouts every 15 s. 503 not_available_yet where it is not configured. */
  presence: (project?: string) =>
    call<WaysPresence>('GET', `/presence${project ? `?project=${enc(project)}` : ''}`),
  /** The design standard every agent builds to: the harness's current version, or the seed Ways
   *  ships with until the harness store is live (`source` says which). */
  designStandard: () => call<DesignStandardAnswer>('GET', '/standards/design'),
  /** The technology standard (Ways 0.38.0, PO-92 EP-10): catalogue by sourcing tier, criteria, standards. */
  technologyStandard: () => call<TechnologyStandardAnswer>('GET', '/standards/technology'),
  /** The adoption register (Ways 0.40.0): what the repository scan found per project, as recommendations. */
  /** Ways 0.41.0: what each tool would do for each project, and why (PO-92 EP-10 FE-3). */
  technologyRecommendations: (q: { project?: string; tool?: string; verdict?: string } = {}) => {
    const qs = new URLSearchParams(Object.entries(q).filter(([, v]) => v) as [string, string][]).toString();
    return call<TechRecommendationsAnswer>('GET', `/technology/recommendations${qs ? `?${qs}` : ''}`);
  },
  /** Ways 0.42.0: fit-for-purpose evaluations on epics and features (PO92-361). */
  technologyEvaluations: (q: { work?: string; category?: string; tool?: string } = {}) => {
    const qs = new URLSearchParams(Object.entries(q).filter(([, v]) => v) as [string, string][]).toString();
    return call<{ evaluations: TechEvaluation[] }>('GET', `/technology/evaluations${qs ? `?${qs}` : ''}`);
  },
  fileEvaluation: (body: TechEvaluationInput) =>
    call<{ evaluation: TechEvaluation }>('POST', '/technology/evaluations', body, true),
  technologyAdoption: (project?: string) =>
    call<TechAdoptionAnswer>('GET', `/technology/adoption${project ? `?project=${enc(project)}` : ''}`),
  /** Propose a tool as a category's standard: one PMO approval, applied only by a super admin's yes. */
  proposeStandard: (body: { tool: string; category: string; work: string; why: string }) =>
    call<{ promotion: TechPromotion }>('POST', '/standards/technology/promotions', body, true),
  claudeEnrollment: () => call<WaysClaudeEnrollment>('GET', '/runner/claude-enrollment'),
  enrollClaude: (token: string) => call<WaysClaudeEnrollment>('PUT', '/runner/claude-enrollment', { token }),
  revokeClaude: () => call<{ revoked: boolean }>('DELETE', '/runner/claude-enrollment'),
};

/** Times are unix seconds (floats); lastUsed is null until the first run; fingerprint is 8 hex. */
export interface WaysQuestion {
  id: string;
  text: string;
  kind: 'choice' | 'yesno' | 'text';
  options?: string[];
  answer?: { by?: string; at?: string; value: string | boolean } | null;
}

/** One response, structured the same way every time (Ways 0.36.17; hs-pmo/ep43#38). */
export interface WaysCard {
  from?: { initials?: string | null; kind?: string; session?: string | null; label?: string | null };
  about?: { item?: string | null; display?: string | null };
  askedAt?: string | null;
  questions?: WaysQuestion[];
  /** The text around the questions: what the asker said, so the channel need not be opened. */
  background?: string | null;
  link?: string | null;
}

export interface WaysDecideItem {
  type: 'ask' | 'approval' | 'hold';
  id: string;
  channel?: string;
  /** Ways sends the asker's initials here; the structured asker is card.from. */
  from?: string | { initials?: string; label?: string };
  fromLabel?: string;
  headline?: string;
  text?: string;
  status?: string;
  questions?: WaysQuestion[];
  summary?: string;
  card?: WaysCard;
  /** Whom it is for: named to me, to a project I own, or to someone else (Ways 0.36.19). */
  for?: 'me' | 'my-project' | 'others';
}

export type WaysPolicyKey = 'pmoGate' | 'waysRouting' | 'keepGoing' | 'planGate';

/** GET /api/policy: the four practice keys as they apply to `project`, with who set them. */
export interface WaysExtraRule { id: string; text: string; level: 'off' | 'recommend' | 'enforce' }

export interface WaysPolicy extends Partial<Record<WaysPolicyKey, string>> {
  /** The wording the plugin shows for each rule (ways-api 0.29.6; hs-pmo/ep19#10). */
  rules?: Partial<Record<WaysPolicyKey, { text: string }>>;
  /** Extra rules beyond the four; a project's list replaces the default's. */
  extra?: WaysExtraRule[];
  project?: string | null;
  setBy?: string | null;
  setAt?: string | null;
}

/** Token counts as Ways reports them; fields beyond `total` vary by source. */
export interface WaysTokens {
  total?: number;
  input?: number;
  output?: number;
  [k: string]: number | undefined;
}

export interface WaysRunnerSession {
  id: string;
  project: string;
  projectKey?: string;
  item?: string | { id?: string; display?: string; title?: string } | null;
  /** "PO92-237" (ways-api 0.29.2+); null on older records. */
  itemKey?: string | null;
  state: string;
  requestedBy?: string;
  actsFor?: string;
  /** Unix seconds (Ways runners.py); not milliseconds. */
  createdAt: number | string;
  usage?: WaysTokens | null;
  lastRequest?: string | { id?: string; kind?: string; ts?: string | number } | null;
  /** Ways 0.36.9+: false on runs that ended in the last 24 h; absent on older Ways (then live). */
  alive?: boolean;
  /** Why an ended run ended (expired/ended/terminated). */
  reason?: string | null;
  /** What a starting run is blocked on (e.g. storage). */
  waiting?: string | null;
  /** "local": a session's own subagent, reported by that session, not a server runner. */
  mode?: string | null;
  parentSession?: string | null;
}

export interface WaysUsageDay { date: string; tokens?: number | WaysTokens; [k: string]: unknown }
export interface WaysUsageProject { project: string; projectKey?: string; tokens?: number | WaysTokens; [k: string]: unknown }

export interface WaysRunnerUsageSession {
  id?: string;
  session?: string;
  project?: string;
  projectKey?: string;
  item?: string | null;
  itemKey?: string | null;
  /** {input, output, cacheRead, cacheWrite, turns}: tokens are input + output. */
  usage?: WaysTokens | null;
  tokens?: number | WaysTokens;
  [k: string]: unknown;
}

export interface WaysReportedSession {
  session: string;
  sub?: string;
  initials?: string;
  project?: string;
  client?: string;
  repository?: string;
  model?: string;
  tokens?: number | WaysTokens;
  turns?: number;
  lastReportedAt?: string;
}

export interface WaysUsage<S> {
  days: WaysUsageDay[];
  projects: WaysUsageProject[];
  sessions: S[];
}

export interface WaysClaudeEnrollment {
  sub?: string;
  enrolled: boolean;
  stale?: boolean;
  enrolledAt?: number | null;
  lastUsed?: number | null;
  expiresAt?: number | null;
  fingerprint?: string | null;
}

export interface WaysSidebarChannel extends WaysChannel {
  type: 'channel';
  id: string;
  state?: string;
  approvals?: number;
  /** PMO approval requests this person may decide in the channel's project. */
  pmoApprovals?: number;
  replies?: number;
  lastUsed?: string | null;
}

/** A project row of the projects view: its channels load when it is opened. */
export interface WaysSidebarProject {
  type: 'project';
  id: string;          // the PMO project _id
  name: string;
  key?: string;
  readOnly?: boolean;
  channels?: number;
  unread?: number;
  approvals?: number;
  pmoApprovals?: number;
  replies?: number;
  lastUsed?: string | null;
  expanded?: boolean;
}

export interface WaysSidebarConversation {
  type: 'conversation';
  id: string;          // "<channel key>#<seq>"
  channel: string;
  channelKey: string;
  channelName?: string;
  seq: number;
  kind?: string;
  headline?: string;
}

export type WaysSidebarItem = WaysSidebarChannel | WaysSidebarConversation | WaysSidebarProject;

export interface WaysSection {
  id: string;          // "attention" | "favorites" | "s-<hex>" | "general"
  title: string;
  builtin: boolean;
  collapsed?: boolean;
  items: WaysSidebarItem[];
  /** Ways 0.36.15+: what the window left out. Active: {count, nextOffset, olderUnread};
   *  General: {count, query: "window=0"}. */
  more?: { count?: number; nextOffset?: number | null; olderUnread?: number; query?: string } | null;
}

export interface WaysSwitch {
  scope: 'global' | 'project' | 'session';
  id?: string | null;
  on: boolean;
  actors: 'agents' | 'all';
  reason: string;
  by: string;        // the switcher's sub
  at: number;        // epoch seconds
}

export interface WaysFinding {
  _id?: string;
  detector: 'rate' | 'loop' | 'conflict' | 'infra' | string;
  severity: string;
  summary: string;
  refs?: unknown;
  count?: number;
  firstSeen?: string | number;
  lastSeen?: string | number;
  session?: string;
  sub?: string;
  project?: string;
}

/** GET /api/presence. Times: `at` and active.since/heartbeatAt are unix seconds; since/until ISO. */
export interface WaysPresenceRow {
  item: string;
  itemKey?: string | null;
  title?: string | null;
  project: string;
  status?: string | null;
  holder: {
    kind: 'runner' | 'agent' | 'session' | string;
    label?: string | null;
    email?: string | null;
    session?: string | null;
    runnerSession?: string;
    sub?: string | null;
    initials?: string | null;
    actsFor?: string | null;
  };
  since?: string | null;
  until?: string | null;
  /** Set while the holder is really working it (a live mark, or a working runner). */
  active?: { by: string; kind: 'session' | 'runner' | string; since: number; heartbeatAt: number } | null;
}

export interface WaysPresence {
  at: number;
  cursor: number;
  items: WaysPresenceRow[];
  projects: string[];
}

/** One principle of the design standard (PO-92 design governance). */
export interface DesignPrinciple {
  id: string;
  group?: string;
  severity?: string;
  verify?: string;
  statement: string;
  why?: string;
  baseline?: boolean;
  [k: string]: unknown;
}

/** A rule with an id and a severity: the standard's components and patterns. */
export interface DesignRule { id: string; severity?: string; rule?: string; [k: string]: unknown }

export interface DesignCheck { id: string; kind?: string; covers?: string[]; threshold?: string; from?: string; [k: string]: unknown }

/** The standard document (hs-ways-to-work src/ways/standards/design-seed-v1.json is v1). */
export interface DesignStandard {
  id?: string;
  version?: string;
  status?: string;
  owner?: string;
  asOf?: string;
  source?: string;
  severities?: Record<string, string>;
  verification?: Record<string, string>;
  appliesTo?: string[];
  principles: DesignPrinciple[];
  tokens?: Record<string, unknown>;
  components?: DesignRule[];
  patterns?: DesignRule[];
  checks?: DesignCheck[];
  baseline?: string;
  [k: string]: unknown;
}

export interface DesignStandardAnswer {
  source: string;
  version?: string;
  standard: DesignStandard;
}

/** GET /api/standards/technology (Ways 0.38.0+; PO-92 EP-10). */
export interface TechTool {
  id: string; name: string; vendor?: string | null; tier: 'oss' | 'nvidia' | 'commercial' | 'internal';
  licence?: string | null; category: string; aliases?: string[]; needsReview?: boolean;
  /** Ways 0.41.0: what it does, and its pages (NGC, developer page, its own site and source). */
  description?: string | null; links?: { label: string; url: string }[]; type?: string | null;
  provenance?: { source: string; sourceType?: string; ref?: string; confidence?: number };
}
export interface TechEvaluationCandidate {
  tool?: string | null; name: string; tier: TechTool['tier']; scores: Record<string, number>;
  notes?: string | null; whyNot?: string | null;
}
export interface TechEvaluationInput {
  work: string; need: string; category: string; choice: string; why: string;
  candidates: { tool?: string; name?: string; tier?: string; scores: Record<string, number>; notes?: string; whyNot?: string }[];
}
export interface TechEvaluation {
  id: string; work: string; workTitle?: string | null; project?: string | null; projectKey?: string | null;
  projectName?: string | null; need: string; category: string; categoryName: string;
  candidates: TechEvaluationCandidate[]; choice: string; choiceName: string; choiceTier: TechTool['tier'];
  why: string; laterTier: boolean; by: string; at: number; history?: { choice: string; why: string; by: string; at: number }[];
}
export type TechVerdict = 'Adopt' | 'Defer' | 'Reject';
export interface TechRecommendation {
  id: string; tool: string; product: string; project?: string | null; projectKey?: string | null; projectName?: string | null;
  relationship: 'support' | 'use-as-part' | 'improve' | 'replace';
  how?: string | null; today?: string | null; verdict?: TechVerdict | null; priority?: string | null; wsjf?: number | null;
  effort?: string | null; horizon?: string | null; scores: Record<string, number | null>;
  governanceGate?: string | null; dependency?: string | null; deployStatus?: string | null; freeNow?: string | null;
  productContext?: { valueProposition?: string | null; nvidiaCurrent?: string | null; nonNvidia?: string | null };
  justification: { reason: string; benefits: string[]; costs: string[];
    overCurrent: { current: string | null; benefit: string | null; complete: boolean } | null };
  toolDetails: Pick<TechTool, 'name' | 'vendor' | 'tier' | 'category' | 'description' | 'links' | 'licence' | 'type'>;
  source: string;
}
export interface TechRecommendationsAnswer {
  recommendations: TechRecommendation[]; asOf?: string; counts: Record<TechVerdict, number>;
  source?: { mapper?: string; mapperVersion?: string };
}
export interface TechCategory {
  id: string; name: string; standard: string | null;
  approved?: { tool: string; approvedBy: string; at: number; decision: string; evaluation?: string; why?: string };
}
export interface TechPromotion {
  tool: string; toolName: string; category: string; categoryName: string; decisionItem?: string;
  display?: string; work?: string; why?: string; at?: number;
}
export interface TechnologyStandardAnswer {
  source: 'harness' | 'seed'; version: string; pending?: TechPromotion[];
  standard: { tiers: { id: string; name: string; order: number; rule: string }[];
    criteria: { id: string; name: string; weight: number; question: string }[];
    categories: TechCategory[]; tools: TechTool[] };
}
export interface TechAdoptionRecord {
  repository: string; tool: string; project?: string | null; projectKey?: string | null; projectName?: string | null;
  status: 'proposed' | 'adopted' | 'trial' | 'not-used' | 'retired';
  evidence?: { path: string; line: string }[];
}
export interface TechAdoptionAnswer {
  records: TechAdoptionRecord[]; scannedAt?: number | null; counts: Record<string, number>;
  byTool: { tool: string; projects: string[]; repositories: number; adopted: number }[];
}
